import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Users,
  Sparkles,
  DollarSign,
  Calendar as CalendarIcon,
  CheckCircle2,
  X,
  CheckSquare,
  Edit2,
  Settings,
  Eye,
  EyeOff,
  Gift,
  Target,
  Trophy,
} from "lucide-react";

export default function DashboardSettingsModal({
  isOpen,
  onClose,
  visibleWidgets,
  onToggleWidget,
  onResetLayout,
  isEditMode,
  onToggleEditMode,
  theme,
}) {
  useEffect(() => {
    const handleEsc = (e) => {
      if (isOpen && e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      setPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y,
      });
    };
    const handleMouseUp = () => {
      isDragging.current = false;
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragStart.current = { x: e.clientX - position.x, y: e.clientY - position.y };
  };

  if (!isOpen) return null;

  const widgets = [
    { id: "students", label: "등록 학생 (KPI)", icon: Users },
    { id: "attendance", label: "출결 현황 (KPI)", icon: CheckCircle2 },
    { id: "birthday", label: "이번달 생일 (KPI)", icon: Gift },
    { id: "budget", label: "남은 예산 (KPI)", icon: DollarSign },
    { id: "schedule", label: "다가오는 일정", icon: CalendarIcon },
    { id: "radar", label: "오늘의 목양 추천", icon: Sparkles },
    { id: "goals", label: "부서 목표", icon: Target },
    { id: "todo", label: "할 일 목록", icon: CheckSquare },
    { id: "growth", label: "사역 여정", icon: Trophy },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-transparent" onClick={onClose}>
      <div
        className={`bg-white/95 backdrop-blur-2xl rounded-[2.5rem] shadow-2xl w-full max-w-sm mx-4 flex flex-col max-h-[85vh] border ${theme.border}/20 overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
        style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
      >
        <div
          className={`p-6 ${theme.bg} backdrop-blur-md text-white flex justify-between items-center shrink-0 shadow-lg z-10 cursor-move`}
          onMouseDown={handleMouseDown}
        >
          <h3 className="font-bold text-xl font-do-hyeon flex items-center gap-2 text-white/90 select-none">
            <Settings size={22} className="animate-spin-slow" /> 대시보드 설정
          </h3>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition duration-300" onMouseDown={(e) => e.stopPropagation()}>
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-4 bg-[#F2F0EA]/80 overflow-y-auto custom-scrollbar relative">
          <div className={`flex items-center justify-between p-4 rounded-2xl shadow-sm border transition-all duration-300 ${isEditMode ? `${theme.bg.replace("bg-", "bg-")}/5 ${theme.border}/20 ring-1 ring-${theme.border.replace("border-", "")}/30` : "bg-white border-gray-100"}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl transition-colors ${isEditMode ? `${theme.bg} text-white` : "bg-gray-100 text-gray-400"}`}>
                <Edit2 size={18} />
              </div>
              <span className={`font-bold ${isEditMode ? theme.text : "text-gray-400"}`}>레이아웃 편집 모드</span>
            </div>
            <button
              onClick={onToggleEditMode}
              className={`relative w-12 h-7 rounded-full transition-colors duration-300 shadow-inner ${isEditMode ? theme.bg : "bg-gray-300"}`}
            >
              <div className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${isEditMode ? "translate-x-5" : "translate-x-0"}`} />
            </button>
          </div>

          <div className={`h-[1px] ${theme.bg.replace("bg-", "bg-")}/10 my-2`}></div>

          <p className={`text-xs ${theme.text}/60 font-black mb-2 px-1`}>표시할 위젯 선택</p>
          {widgets.map((widget) => (
            <div key={widget.id} className={`flex items-center justify-between p-4 rounded-2xl shadow-sm border transition-all hover:scale-[1.02] ${visibleWidgets[widget.id] ? `bg-white ${theme.border}/20 ring-1 ring-${theme.border.replace("border-", "")}/10` : "bg-gray-50/50 border-gray-100 opacity-70 hover:opacity-100"}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl transition-colors ${visibleWidgets[widget.id] ? `${theme.bg.replace("bg-", "bg-")}/10 ${theme.text}` : "bg-gray-100 text-gray-400"}`}>
                  <widget.icon size={18} />
                </div>
                <span className={`font-bold transition-colors ${visibleWidgets[widget.id] ? "text-gray-800" : "text-gray-400"}`}>{widget.label}</span>
              </div>
              <button
                onClick={() => onToggleWidget(widget.id)}
                className={`p-2 rounded-full transition-all shadow-sm ${visibleWidgets[widget.id] ? `${theme.bg} text-white hover:opacity-80` : "bg-gray-200 text-gray-400 hover:bg-gray-300"}`}
              >
                {visibleWidgets[widget.id] ? <Eye size={16} /> : <EyeOff size={16} />}
              </button>
            </div>
          ))}
        </div>
        <div className={`p-5 bg-white/90 backdrop-blur-md border-t ${theme.border}/10 flex flex-col gap-3 shrink-0 z-10`}>
          <button onClick={onClose} className={`w-full py-3.5 ${theme.bg} text-white font-bold rounded-2xl hover:opacity-90 transition shadow-lg ${theme.shadow}/20 active:scale-95 transform duration-200`}>
            확인 완료
          </button>
          <button onClick={onResetLayout} className="w-full py-2 text-gray-400 text-xs font-bold hover:text-red-500 transition">
            레이아웃 초기화
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
